/**
 * This function normalises a given route by removing any leading segments related to the application context,
 * such as '/apply/application/{uni}/{applicantId}' or '/apply/staff/application/{uni}/{applicantId}'.
 * It ensures that the returned route starts with a '/' for consistency.
 * @param route - The route to normalise.
 * @returns
 */
export function normaliseRoute(route: string): string {
  const agentPrefixAdded = new RegExp('^/?apply/application/');
  const agentPrefixWithParams = new RegExp('^/?apply/application/[a-zA-Z0-9_-]+/[a-zA-Z0-9_-]+');
  const rootPrefixAdded = new RegExp('^/?apply/');

  /**
   * Normalisation steps to seperate the relative route from the absolute base path...
   */

  let url = decodeURIComponent(route ?? ''); // decode URL-encoded strings

  // normalise next and previous routes beginning with /apply/application/{uni}/{applicantId} in agent view
  if (agentPrefixAdded.test(url)) {
    url = url.replace(agentPrefixWithParams, '');
  }

  // normalise next and previous routes beginning with /apply
  if (rootPrefixAdded.test(url)) {
    url = url.replace(rootPrefixAdded, '');
  }
  // normalise next and previous routes not beginning with /
  if (!url?.startsWith('/')) {
    url = `/${url}`;
  }

  return url;
}

export interface IFlowSpecificUrlProps {
  url: string | undefined;
  params: { uni?: string; applicantId?: string };
  currentView: 'applicant' | 'staff' | 'agent';
}

/**
 * This function generates a flow-specific URL based on the provided parameters and the current view context.
 * It adjusts the URL structure depending on whether the view is for an applicant, staff member, or agent.
 * @param url
 * @param pathname
 * @param params
 * @param currentView
 * @returns
 */
export function generateFlowSpecificUrl(props: IFlowSpecificUrlProps): string | undefined {
  const { url, params, currentView } = props;
  const { uni, applicantId } = params;

  const route = normaliseRoute(url ?? '');

  if (currentView === 'agent') {
    return `/apply/application/${uni}/${applicantId}${route}`;
  }

  if (currentView === 'staff') {
    return `/apply/staff/application/${uni}/${applicantId}${route}`;
  }

  return `/apply${route}`;
}
